import { type FC, type ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { isPartnerPrize } from '@/entities/wheel/helpers/predicates';
import { type Prize, WHEEL_PRIZES } from '@/entities/wheel/model/wheel.types';

import { ReactComponent as Stars } from '@/assets/images/stars.svg?react';

import { PRIORITY_BY_TYPE, PRIZE_NAME_BY_TYPE } from '../../constants';
import { type SimplePrize } from '../../types';
import { PrizeCard } from '../prize-card/PrizeCard';

import s from './MultiplePrizes.module.scss';

interface Props {
  className?: string;
  title?: ReactNode;
  prizes: Pick<
    Prize,
    | 'id'
    | 'type'
    | 'name'
    | 'partSize'
    | 'collectedPartsCount'
    | 'targetPartsCount'
    | 'prizeId'
  >[];
}

const MAX_COUNT_TO_RENDER = 7;

const sortPrizes = <T extends SimplePrize>(prizes: T[]) => {
  const hasSuperPrize = prizes.some((prize) => prize.isSuperPrize);

  // Если нет суперприза — помечаем первый CLICKER-приз как "виртуальный суперприз"
  let clickerPromotedType: WHEEL_PRIZES | null = null;
  if (!hasSuperPrize) {
    const clickerPrize = prizes.find(
      (p) =>
        p.type === WHEEL_PRIZES.CLICKER_XP || p.type === WHEEL_PRIZES.CLICKER_PLAYCOIN
    );

    const firstLinePrize = clickerPrize;
    if (firstLinePrize) {
      clickerPromotedType = firstLinePrize.type;
    }
  }

  const sorted = prizes.sort((a, b) => {
    const aIsSuper = a.isSuperPrize || (!hasSuperPrize && a.type === clickerPromotedType);
    const bIsSuper = b.isSuperPrize || (!hasSuperPrize && b.type === clickerPromotedType);

    if (aIsSuper && !bIsSuper) return -1;
    if (!aIsSuper && bIsSuper) return 1;

    const defaultPriorityA = PRIORITY_BY_TYPE[a.type];
    const defaultPriorityB = PRIORITY_BY_TYPE[b.type];

    return defaultPriorityA - defaultPriorityB;
  });

  const left: T[] = [];
  const right: T[] = [];

  const types: WHEEL_PRIZES[] = [];

  const repeatsLeft: T[] = [];
  const repeatsRight: T[] = [];

  sorted.forEach((prize, index) => {
    if (types.includes(prize.type)) {
      if (index % 2) {
        repeatsRight.push(prize);
      } else {
        repeatsLeft.push(prize);
      }
    } else {
      if (index % 2) {
        right.push(prize);
      } else {
        left.push(prize);
      }
    }

    types.push(prize.type);
  });

  return [
    [...[...repeatsLeft].reverse(), ...[...left].reverse()],
    [...right, ...repeatsRight],
  ];
};

const sliceMiddle = <T extends Pick<SimplePrize, 'type'>>(
  arr: T[],
  count: number
): T[] => {
  const length = arr.length;

  const start = Math.floor(Math.max(0, length - count) / 2);

  return arr.slice(start, start + count);
};

export const MultiplePrizes: FC<Props> = ({
  className,
  title,
  prizes: prizesProp = [],
}) => {
  const { t } = useTranslation();

  const [left, right] = sortPrizes(prizesProp as SimplePrize[]);

  const prizes = [...left, ...right];

  const cardPrizes = sliceMiddle(prizes, MAX_COUNT_TO_RENDER);

  const [middleStart, middleEnd] = useMemo<[number, number]>(() => {
    if (!cardPrizes.length) return [0, 0];

    if (cardPrizes.length % 2 === 0) {
      const totalMiddleElements = 2;

      const start = Math.floor((cardPrizes.length - totalMiddleElements) / 2);
      const end = start + totalMiddleElements - 1;

      return [start, end];
    } else {
      const middle = Math.floor(cardPrizes.length / 2);
      return [middle, middle];
    }
  }, [cardPrizes.length]);

  const isMiddleCard = (index: number) => {
    return index >= middleStart && index <= middleEnd;
  };

  const getOffsetIndex = (index: number) => {
    if (isMiddleCard(index)) return 0;

    if (index < middleStart) return middleStart - index;

    return index - middleEnd;
  };

  const getPriority = (index: number) => {
    if (isMiddleCard(index)) return middleStart + 1;

    if (index < middleStart) return index + 1;
    return cardPrizes.length - index;
  };

  const getSide = (index: number) => {
    if (isMiddleCard(index)) return 'middle';

    if (index < middleStart) return 'left';
    return 'right';
  };

  const hasPartnerPrize = !!prizes.find(isPartnerPrize);

  return (
    <>
      <div className={clsx(s.container, className)}>
        {!hasPartnerPrize && <Stars className={s.stars} />}
        {!!title && <span className={s.title}>{title}</span>}
        <div className={s.list}>
          {cardPrizes.map((prize, index) => {
            return (
              <PrizeCard
                key={prize.id}
                prize={prize}
                isSingleMiddle={middleStart === 0 || middleStart === middleEnd}
                isMiddle={isMiddleCard(index)}
                priority={getPriority(index)}
                offsetIndex={getOffsetIndex(index)}
                side={getSide(index)}
                totalLength={cardPrizes.length}
              />
            );
          })}
        </div>
        <div className={s.names}>
          {[...left.reverse().flatMap((l, i) => [l, right[i]])].map((prize, index) => {
            if (!prize) return null;
            return (
              <div
                key={prize.id}
                className={clsx(
                  s['name-wrapper'],
                  prize.isSuperPrize && s['super-prize']
                )}
              >
                <span className={s.name}>
                  {prize.isSuperPrize && '🎁'}
                  <span
                    className={s['name-text']}
                    style={{ animationDelay: `${0.05 * index}s` }}
                  >
                    {PRIZE_NAME_BY_TYPE[prize.type](prize, t)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
